/**
 * environmentInfo — Detects runtime environment details for feedback submission.
 *
 * Captures device type (mobile/web), platform info (OS + browser), and
 * the current URL domain. Used to enrich GitHub issue bodies with context
 * about where the feedback was recorded.
 */

import type { EnvironmentInfo } from "../types/feedback";

/** Detect whether the current device is mobile or web (desktop). */
function detectDeviceType(): "mobile" | "web" {
  const ua = navigator.userAgent;
  const mobileKeywords =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  if (mobileKeywords.test(ua)) return "mobile";

  // Also check viewport width as a fallback
  if (typeof window !== "undefined" && window.innerWidth < 768) return "mobile";

  return "web";
}

/** Extract platform information from user agent. */
function detectPlatform(): string {
  const ua = navigator.userAgent;

  // Android
  const androidMatch = ua.match(/Android\s([\d.]+)/);
  if (androidMatch) return `Android ${androidMatch[1]}`;

  // iOS
  const iosMatch = ua.match(/(?:iPhone|iPad|iPod).*?OS\s([\d_]+)/);
  if (iosMatch) return `iOS ${iosMatch[1].replace(/_/g, ".")}`;

  // macOS
  const macMatch = ua.match(/Mac OS X\s([\d._]+)/);
  const macVersion = macMatch ? macMatch[1].replace(/_/g, ".") : "";

  // Browser detection
  const edgeMatch = ua.match(/Edg\/([\d.]+)/);
  if (edgeMatch)
    return macVersion
      ? `macOS ${macVersion} / Edge ${edgeMatch[1]}`
      : `Edge ${edgeMatch[1]}`;

  const chromeMatch = ua.match(/Chrome\/([\d.]+)/);
  if (chromeMatch)
    return macVersion
      ? `macOS ${macVersion} / Chrome ${chromeMatch[1]}`
      : `Chrome ${chromeMatch[1]}`;

  const firefoxMatch = ua.match(/Firefox\/([\d.]+)/);
  if (firefoxMatch)
    return macVersion
      ? `macOS ${macVersion} / Firefox ${firefoxMatch[1]}`
      : `Firefox ${firefoxMatch[1]}`;

  const safariMatch = ua.match(/Version\/([\d.]+).*Safari/);
  if (safariMatch)
    return macVersion
      ? `macOS ${macVersion} / Safari ${safariMatch[1]}`
      : `Safari ${safariMatch[1]}`;

  // Windows
  const windowsMatch = ua.match(/Windows NT\s([\d.]+)/);
  if (windowsMatch) return `Windows ${windowsMatch[1]}`;

  return ua.slice(0, 80);
}

/** Extract URL domain from current location. */
function detectUrlDomain(): string {
  if (typeof window === "undefined") return "unknown";
  return window.location.hostname || "localhost";
}

/** Capture a snapshot of the current runtime environment. */
export function captureEnvironmentInfo(): EnvironmentInfo {
  return {
    deviceType: detectDeviceType(),
    platform: detectPlatform(),
    urlDomain: detectUrlDomain(),
  };
}
